import { useState, useRef, useCallback, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import type { SearchResult } from '../types/navigation'
import { performGlobalSearch } from '../utils/globalSearch'

export function useGlobalSearch() {
  const navigate = useNavigate()
  const [searchQuery, setSearchQuery] = useState('')
  const [searchResults, setSearchResults] = useState<SearchResult[]>([])
  const [showSearchResults, setShowSearchResults] = useState(false)
  const [searchLoading, setSearchLoading] = useState(false)
  const searchRef = useRef<HTMLDivElement>(null)
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null)

  // Debounced search function
  const performSearch = useCallback(async (query: string) => {
    if (!query.trim()) {
      setSearchResults([])
      setShowSearchResults(false)
      return
    }

    setSearchLoading(true)
    try {
      const results = await performGlobalSearch(query)
      setSearchResults(results)
      setShowSearchResults(true)
    } catch (err) {
      console.error('Search failed:', err)
      setSearchResults([])
      setShowSearchResults(true) // Still show dropdown with "no results" message
    } finally {
      setSearchLoading(false)
    }
  }, [])

  // Handle search input change with debounce
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const query = e.target.value
    setSearchQuery(query)

    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current)
    }

    searchTimeoutRef.current = setTimeout(() => {
      performSearch(query)
    }, 300)
  }

  // Handle result click
  const handleResultClick = (result: SearchResult) => {
    setShowSearchResults(false)
    setSearchQuery('')
    switch (result.type) {
      case 'property':
        navigate(`/properties/${result.id}`)
        break
      case 'issue':
        navigate(`/maintenance/${result.id}`)
        break
      case 'tenancy':
        navigate(`/tenants/${result.id}`)
        break
      case 'prospect':
        navigate(`/prospects/${result.id}`)
        break
    }
  }

  // Close search results when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(e.target as Node)) {
        setShowSearchResults(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  return {
    searchQuery,
    setSearchQuery,
    searchResults,
    setSearchResults,
    showSearchResults,
    setShowSearchResults,
    searchLoading,
    searchRef,
    handleSearchChange,
    handleResultClick,
  }
}
